import { useState } from "react";
import { Card } from "./ui/Card";
import { CondoCase, IssueType } from "types/CondoCase";
import fetchWithCredentials from "apiClient";
import { useAuth } from "context/AuthContext";

interface CaseSummaryProps {
  case: CondoCase;
  onUpdate?: (updatedCase: CondoCase) => void;
}

const TYPE_LABELS_ES: Record<IssueType, string> = {
  issue_high: "Alta prioridad",
  issue_medium: "Prioridad media",
  issue_low: "Baja prioridad",
};

const TYPE_LABELS_EN: Record<IssueType, string> = {
  issue_high: "High priority",
  issue_medium: "Medium priority",
  issue_low: "Low priority",
};

const STATUS_LABELS_ES: Record<CondoCase["status"], string> = {
  open: "Abierto",
  closed: "Cerrado",
};

const STATUS_LABELS_EN: Record<CondoCase["status"], string> = {
  open: "Open",
  closed: "Closed",
};

const VALID_PRIORITY_TYPES: IssueType[] = [
  "issue_high",
  "issue_medium",
  "issue_low",
];

export function CaseSummary({ case: caseItem, onUpdate }: CaseSummaryProps) {
  const { lang } = useAuth();
  const locale = lang === "es" ? "es" : "en";

  const TYPE_LABELS =
    locale === "es" ? TYPE_LABELS_ES : TYPE_LABELS_EN;
  const STATUS_LABELS =
    locale === "es" ? STATUS_LABELS_ES : STATUS_LABELS_EN;

  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePriorityChange = async (newType: IssueType) => {
    if (newType === caseItem.type || updating) return;

    setUpdating(true);
    setError(null);
    try {
      const updatedCase = await fetchWithCredentials(
        `/manager-api/cases/${caseItem.id}/`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ type: newType }),
        }
      );
      onUpdate?.(updatedCase);
    } catch (err: any) {
      console.error(err);
      setError(
        err.message ||
          (locale === "es"
            ? "Error al actualizar la prioridad"
            : "Failed to update priority")
      );
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Card className="p-4">
      <div className="mb-2 flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-semibold text-gray-800">
            {caseItem.title}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {caseItem.condoName}
            {caseItem.assetName ? ` • ${caseItem.assetName}` : ""}
          </p>
          {caseItem.reportedBy ? (
            <p className="mt-1 text-sm text-gray-500">
              {locale === "es" ? "Reportado por" : "Reported by"}{" "}
              {caseItem.reportedBy.name}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          <span
            className={`rounded px-2 py-1 text-xs font-medium ${
              caseItem.status === "open"
                ? "bg-green-100 text-green-800"
                : "bg-gray-100 text-gray-800"
            }`}
          >
            {STATUS_LABELS[caseItem.status]}
          </span>
          {VALID_PRIORITY_TYPES.includes(caseItem.type as IssueType) ? (
            <select
              value={caseItem.type}
              onChange={(e) =>
                handlePriorityChange(e.target.value as IssueType)
              }
              disabled={updating}
              className={`cursor-pointer rounded border-none px-2 py-1 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50 ${
                caseItem.type === "issue_high"
                  ? "bg-red-100 text-red-800"
                  : caseItem.type === "issue_medium"
                    ? "bg-orange-100 text-orange-800"
                    : caseItem.type === "issue_low"
                      ? "bg-yellow-100 text-yellow-800"
                      : "bg-gray-100 text-gray-800"
              }`}
            >
              {VALID_PRIORITY_TYPES.map((type) => (
                <option key={type} value={type}>
                  {TYPE_LABELS[type]}
                </option>
              ))}
            </select>
          ) : (
            <span
              className={`rounded px-2 py-1 text-xs font-medium ${
                caseItem.type === "issue_high"
                  ? "bg-red-100 text-red-800"
                  : caseItem.type === "issue_medium"
                    ? "bg-orange-100 text-orange-800"
                    : caseItem.type === "issue_low"
                      ? "bg-yellow-100 text-yellow-800"
                      : "bg-gray-100 text-gray-800"
              }`}
            >
              {TYPE_LABELS[caseItem.type] || caseItem.type}
            </span>
          )}
        </div>
      </div>
      {caseItem.status === "open" ? (
        <p className="text-sm leading-snug text-gray-500">
          {locale === "es"
            ? "Para cerrar este incidente, pedíselo a Eloria."
            : "To close this incident, ask Eloria in chat."}
        </p>
      ) : null}
      {error && (
        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
          {error}
        </div>
      )}
      {caseItem.summary && (
        <p className="text-sm text-gray-600 mt-2">{caseItem.summary}</p>
      )}
    </Card>
  );
}
